<script setup lang="ts">
import { computed, onBeforeMount, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BaseIcon from '@/components/base/baseIcon.vue'
import BaseMenuBarMobile from '@/components/base/BaseMenuBarMobile.vue'
import linearLoadingCard from '@/components/loading/linearLoadingCard.vue'

import { getTotalRequestCommunityResearch } from '@/service/contributor/research'
import { iconNames } from '@/enums/iconNames'
import { moduleName } from '@/enums/moduleNames'
import { pageNames } from '@/enums/pageNames'
import { userPermissionStore } from '@/stores/user/permission'
import { communityResearchStore } from '@/stores/communityResearch/communityResearch'

const route = useRoute()
const router = useRouter()
const communityResearch = communityResearchStore()

enum mainMenu {
  NONE = 'NONE',
  USER = 'USER',
  COMMUNITY = 'COMMUNITY',
  RESEARCH = 'RESEARCH',
  INSTITUTION = 'INSTITUTION',
  NEWS = 'NEWS',
  SUBSCRIPTION = 'SUBSCRIPTION',
  TOOLS = 'TOOLS',
  ADBANNER = 'ADBANNER',
}

interface menuItem {
  pageName: pageNames | null
  pageLabel: string
  module: moduleName | moduleName[] | null
  icon: iconNames | null
  tag: mainMenu | null
  submenu: menuItem[]
  subMenuActive: boolean
  pageGroup: pageNames[]
}
const menuSets = ref<menuItem[]>([
  {
    pageName: pageNames.HOME,
    module: null,
    pageLabel: 'Dashboard',
    icon: iconNames.MENUICON,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [pageNames.HOME]
  },
  {
    pageName: pageNames.USER,
    module: moduleName.INTERNAL_USER,
    pageLabel: 'Internal User',
    icon: iconNames.INTERNALUSER,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.USER,
      pageNames.USEREDITOREXISTING,
      pageNames.USEREDITORNEW,
      pageNames.PERMISSION,
      pageNames.PERMISSIONEDITOREXISTING,
      pageNames.PERMISSIONEDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Circle',
    icon: iconNames.COMMUNITY,
    tag: mainMenu.COMMUNITY,
    submenu: [
      {
        pageName: pageNames.COMMUNITYRESEARCHCONTENT,
        module: moduleName.CIRCLE_RESEARCH,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.COMMUNITY,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.COMMUNITYRESEARCHCONTENT, pageNames.COMMUNITYRESEARCHEDITOREXISTING]
      },
      {
        pageName: pageNames.CONTRIBUTOR,
        module: moduleName.CONTENT_CONTRIBUTOR,
        pageLabel: 'Contributor',
        icon: null,
        tag: mainMenu.COMMUNITY,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.CONTRIBUTOR,
          pageNames.CONTRIBUTOREDITOREXISTING,
          pageNames.CONTRIBUTOREDITORNEW
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.COMMUNITYRESEARCHCONTENT,
      pageNames.COMMUNITYRESEARCHEDITOREXISTING,
      pageNames.CONTRIBUTOR,
      pageNames.CONTRIBUTOREDITOREXISTING,
      pageNames.CONTRIBUTOREDITORNEW,
      pageNames.CONTRIBUTOREDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Institution',
    icon: iconNames.INSTITUTION,
    tag: mainMenu.INSTITUTION,
    submenu: [
      {
        pageName: pageNames.INSTITUTIONCONTENT,
        module: moduleName.INSTITUTION_CONTENT,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.INSTITUTIONCONTENT,
          pageNames.INSTITUTIONCONTENTEDITOREXISTING,
          pageNames.INSTITUTIONCONTENTEDITORNEW
        ]
      },
      {
        pageName: pageNames.INSTITUTION,
        module: moduleName.INSTITUTION,
        pageLabel: 'Broker Data',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.INSTITUTION, pageNames.INSTITUTIONEDITORNEW, pageNames.INSTITUTIONEDITOREXISTING]
      },
      {
        pageName: pageNames.INSTITUTIONSTOCKPLACEMENT,
        module: moduleName.INSTITUTION_STOCK_PLACEMENT,
        pageLabel: 'Selection Stocks',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.INSTITUTIONSTOCKPLACEMENT,
          pageNames.INSTITUTIONSTOCKPLACEMENTDETAIL,
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.INSTITUTION,
      pageNames.INSTITUTIONEDITORNEW,
      pageNames.INSTITUTIONEDITOREXISTING,
      pageNames.INSTITUTIONCONTENT,
      pageNames.INSTITUTIONCONTENTEDITOREXISTING,
      pageNames.INSTITUTIONCONTENTEDITORNEW,
      pageNames.INSTITUTIONSTOCKPLACEMENT,
      pageNames.INSTITUTIONSTOCKPLACEMENTDETAIL,
    ]
  },
  {
    pageName: pageNames.STREETCALLCONTENT,
    module: moduleName.STREETCALL_CONTENT,
    pageLabel: 'Streetcall',
    icon: iconNames.INSTITUTION,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.STREETCALLCONTENT,
      pageNames.STREETCALLCONTENTEDITOREXISTING,
      pageNames.STREETCALLCONTENTEDITORNEW
    ]
  },
  {
    pageName: pageNames.FFSCONTENT,
    module: moduleName.FUND_FACT_SHEET_CONTENT,
    pageLabel: 'Fund Fact Sheet',
    icon: iconNames.INSTITUTION,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.FFSCONTENT,
      pageNames.FFSCONTENTEDITOREXISTING,
      pageNames.FFSCONTENTEDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Exclusive',
    icon: iconNames.RESEARCH,
    tag: mainMenu.RESEARCH,
    submenu: [
      {
        pageName: pageNames.ALGORESEARCH,
        module: moduleName.ALGO_RESEARCH,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.RESEARCH,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.ALGORESEARCH,
          pageNames.ALGORESEARCHEDITOREXISTING,
          pageNames.ALGORESEARCHEDITORNEW
        ]
      },
      {
        pageName: pageNames.RESEARCHBLAST,
        module: moduleName.ALGO_RESEARCH_BLAST,
        pageLabel: 'Research Blast',
        icon: null,
        tag: mainMenu.RESEARCH,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.RESEARCHBLAST,
          pageNames.RESEARCHBLASTNEW,
          pageNames.RESEARCHBLASTEDITOREXISTING
        ]
      },
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.ALGORESEARCH,
      pageNames.ALGORESEARCHEDITOREXISTING,
      pageNames.RESEARCHBLAST,
      pageNames.RESEARCHBLASTNEW,
      pageNames.RESEARCHBLASTEDITOREXISTING,
      pageNames.ALGORESEARCHEDITORNEW,
      // pageNames.NEWSLETTER,
      // pageNames.NEWSLETTEREDITORUPDATE,
      // pageNames.NEWSLETTERDETAILEXISTING,
      // pageNames.CATEGORY,
      // pageNames.CATEGORYEDITORNEW,
      // pageNames.CATEGORYEDITOREXISTING,
      // pageNames.MASTERTAG,
      // pageNames.MASTERTAGNEW
    ]
  },
  {
    pageName: pageNames.ALGONEWS,
    module: moduleName.ALGO_NEWS,
    pageLabel: 'Insight',
    icon: iconNames.NEWS,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.ALGONEWS,
      pageNames.ALGONEWSEDITOREXISTING,
      pageNames.ALGONEWSEDITORNEW
    ]
  },
  {
    pageName: pageNames.CLIENT,
    module: moduleName.CLIENT,
    pageLabel: 'Client',
    icon: iconNames.CLIENT,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [pageNames.CLIENT]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Subscriptions',
    icon: iconNames.SUBSCRIPTION,
    tag: mainMenu.SUBSCRIPTION,
    submenu: [
      {
        pageName: pageNames.SUBSCRIPTION,
        module: [moduleName.SUBSCRIPTION],
        pageLabel: 'Master Data',
        icon: null,
        tag: mainMenu.SUBSCRIPTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.SUBSCRIPTION,
          pageNames.SUBSCRIPTIONCREATE
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.SUBSCRIPTION, 
      pageNames.SUBSCRIPTIONCREATE, 
      pageNames.SUBSCRIPTIONDETAIL
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Tools',
    icon: iconNames.TOOL,
    tag: mainMenu.TOOLS,
    submenu: [
      {
        pageName: pageNames.STOCKS,
        module: [
          moduleName.INDEX_EDITOR,
          moduleName.STOCK_PLACEMENT,
          moduleName.MASTER_STOCK_INDEX
        ],
        pageLabel: 'Stocks',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.STOCKS]
      },
      {
        pageName: pageNames.HYBRIDSLIDER,
        module: moduleName.HYBRID_SLIDER,
        pageLabel: 'Content Placement',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.HYBRIDSLIDER,
          pageNames.HYBRIDSLIDERCREATE,
          pageNames.HYBRIDSLIDERDETAIL
        ]
      },
      {
        pageName: pageNames.TEXTSLIDER,
        module: moduleName.TEXT_SLIDER,
        pageLabel: 'Text Slider',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.TEXTSLIDER, pageNames.TEXTSLIDERCREATE, pageNames.TEXTSLIDERDETAIL]
      },
      {
        pageName: pageNames.NEWSLETTER,
        module: moduleName.NEWS_LETTER,
        pageLabel: 'Newsletter',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.NEWSLETTER,
          pageNames.NEWSLETTEREDITORUPDATE,
          pageNames.NEWSLETTERDETAILEXISTING
        ]
      },
      {
        pageName: pageNames.CATEGORY,
        module: moduleName.RESEARCH_CATEGORY,
        pageLabel: 'Category',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.CATEGORY,
          pageNames.CATEGORYEDITORNEW,
          pageNames.CATEGORYEDITOREXISTING
        ]
      },
      {
        pageName: pageNames.MASTERTAG,
        module: moduleName.MASTER_RESEARCH_TAG,
        pageLabel: 'Master Tag',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.MASTERTAG, pageNames.MASTERTAGNEW]
      },
      {
        pageName: pageNames.ACCOUNT_WALL,
        module: moduleName.ACCOUNT_WALL,
        pageLabel: 'Account Wall',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.ACCOUNT_WALL]
      },
      {
        pageName: pageNames.LEGAL,
        module: [moduleName.CIRCLE_DISCLOSURE, moduleName.LEGAL_INFORMATION],
        pageLabel: 'Legal',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.LEGAL, pageNames.LEGALCIRCLEDISCLOSURE, pageNames.LEGALCIRCLEDISCLOSURENEW, pageNames.LEGALINFORMATION, pageNames.LEGALINFORMATIONEDITOR]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.HYBRIDSLIDER,
      pageNames.HYBRIDSLIDERCREATE,
      pageNames.HYBRIDSLIDERDETAIL,
      pageNames.STOCKS,
      pageNames.TEXTSLIDER,
      pageNames.TEXTSLIDERCREATE,
      pageNames.TEXTSLIDERDETAIL,
      pageNames.NEWSLETTER,
      pageNames.NEWSLETTEREDITORUPDATE,
      pageNames.NEWSLETTERDETAILEXISTING,
      pageNames.CATEGORY,
      pageNames.CATEGORYEDITORNEW,
      pageNames.CATEGORYEDITOREXISTING,
      pageNames.MASTERTAG,
      pageNames.MASTERTAGNEW,
      pageNames.ACCOUNT_WALL,
      pageNames.LEGAL,
      pageNames.LEGALCIRCLEDISCLOSURE,
      pageNames.LEGALCIRCLEDISCLOSURENEW, 
      pageNames.LEGALINFORMATION,
      pageNames.LEGALINFORMATIONEDITOR
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Ad',
    icon: iconNames.ADS,
    tag: mainMenu.ADBANNER,
    submenu: [
      {
        pageName: pageNames.ADBANNER,
        module: moduleName.AD_BANNER,
        pageLabel: 'Master Ad',
        icon: null,
        tag: mainMenu.ADBANNER,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.ADBANNER, pageNames.ADBANNEREDITOREXISTING, pageNames.ADBANNEREDITORNEW]
      },
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.ADBANNER,
      pageNames.ADBANNEREDITOREXISTING,
      pageNames.ADBANNEREDITORNEW,
    ]
  },
])

const userPermission = userPermissionStore()

const menuShrinkedState = localStorage.getItem('menuShrinked') || 'N'
const shrinked = ref<boolean>(menuShrinkedState === 'Y')

// const subShrinkedCommunity = ref(false)
// const subShrinkedResearch = ref(false)
const mobileMenuActive = ref(false)
const currentRouteName = computed(() => route.name as pageNames)
// const subMenuUser = ref(false)
// const subMenuCommunity = ref(false)
// const subMenuResearch = ref(false)

const fetchState = reactive({
  totalRequest: true
})

function toggleTextMenubar(): void {
  hideSubmenuExcept(mainMenu.NONE)
  shrinked.value = !shrinked.value
  // subShrinkedCommunity.value = shrinked.value
  // subShrinkedResearch.value = shrinked.value
  localStorage.setItem('menuShrinked', shrinked.value ? 'Y' : 'N')
}
function hideSubmenuExcept(page: mainMenu) {
  // subMenuUser.value = page === mainMenu.USER ? !subMenuUser.value : false
  // subMenuCommunity.value = page === mainMenu.COMMUNITY ? !subMenuCommunity.value : false
  // subMenuResearch.value = page === mainMenu.RESEARCH ? !subMenuResearch.value : false

  //new implementation
  menuSets.value.forEach((item) => {
    item.subMenuActive = item.tag === page ? !item.subMenuActive : false
  })
}

function hasPermission(permission: moduleName): boolean {
  return userPermission.getUserPermission.read_action.includes(permission)
}
function hasPermissions(permission: moduleName[]): boolean {
  if (permission.length === 0) {
    return true
  }

  for (var x = 0; x < permission.length; x++) {
    if (userPermission.getUserPermission.read_action.includes(permission[x])) {
      return true
    }
  }
  return false
}
// function getPageNamesFromSubMenu(menu: menuItem[], parent: pageNames) {
//   let pages: pageNames[] = []
//   pages.push(parent)
//   for (var x = 0; x < menu.length; x++) {
//     pages.push(menu[x].pageName)
//   }

//   return pages
// }
function getModules(menu: menuItem[], parent: moduleName | null) {
  let modules: moduleName[] = []
  if (parent !== null) {
    modules.push(parent)
  }
  for (var x = 0; x < menu.length; x++) {
    if (menu[x].module !== null) {
      if (Array.isArray(menu[x].module!)) {
        modules = modules.concat(menu[x].module!)
      } else {
        modules.push(menu[x].module! as moduleName)
      }
    }
  }
  return modules
}

async function fetchTotalRequestCommunityResearch() {
  fetchState.totalRequest = true 
  const response = await getTotalRequestCommunityResearch()
  if (response.data.data?.totalRequest !== undefined) {
    communityResearch.totalCircleRequest = response.data.data.totalRequest
  }
  fetchState.totalRequest = false 
}

// onMounted(() => {
//   localStorage.getItem('menuShrinked')
//   if (
//     window.innerWidth <= 1024 &&
//     localStorage.getItem('menuShrinked') === null &&
//     !shrinked.value
//   ) {
//     toggleTextMenubar()
//   }
// })

function setShrinked(val: boolean) {
  shrinked.value = val
  localStorage.setItem('menuShrinked', val ? 'Y' : 'N')
}

onMounted(() => {
  if (window.innerWidth <= 1024 && localStorage.getItem('menuShrinked') === null) {
    setShrinked(true)
  }
  window.addEventListener('resize', () => {
    if (window.innerWidth <= 1024 && !shrinked.value) {
      setShrinked(true)
    }
  })
})

onBeforeMount(async () => {
  fetchTotalRequestCommunityResearch()
})
</script>
<template>
  <div
    class="md:hidden flex flex-col items-center w-full bg-white"
    :class="[mobileMenuActive ? 'fixed h-svh z-100 overflow-y-scroll overscroll-contain' : '']"
  >
    <div class="md:hidden px-4 py-5 flex flex-row w-full justify-between" :class="[mobileMenuActive ? 'bg-[--algo-brand-hadfield-blue-800]' : '']">
      <div :class="[mobileMenuActive ? 'logo-menubar' : 'logo-menubar-primary']"  @click="router.push({ name: pageNames.HOME })"></div>
      <button type="button" @click="() => (mobileMenuActive = !mobileMenuActive)">
        <BaseIcon v-if="!mobileMenuActive" :icon-name="iconNames.NAVIGATION" :size="24"/>
        <BaseIcon v-else :icon-name="iconNames.EXIT" :size="24" hex-color="#FEFEFE"/>
      </button>
    </div>
    <div class="relative w-full" v-show="mobileMenuActive">
      <BaseMenuBarMobile />
    </div>
  </div>

  <div
    :class="[shrinked ? 'menubar-small' : 'max-[1023px]:w-[116px] lg:w-[248px] menubar']"
    class="hidden md:block w-full absolute md:relative bg-[--algo-brand-hadfield-blue-800]"
  >
    <div
      :class="[shrinked ? 'px-4' : 'px-6']"
      class="flex py-7-5 h-26 justify-center lg:justify-between padding-logo sticky top-0 bg-[--algo-brand-hadfield-blue-800] z-50"
    >
      <div
        :class="[shrinked ? 'logo-menubar-with-text' : 'logo-menubar']"
        class=""
        @click="router.push({ name: pageNames.HOME })"
      ></div>
      <button @click="toggleTextMenubar()" class="hidden lg:block">
        <div :class="[shrinked ? 'menubar-nav-right' : 'menubar-nav-left']"></div>
      </button>
      <div class="text-right md:hidden">
        <button type="button"></button>
      </div>
    </div>
    <!-- <div class="border-t-[1px] border-slate-300"></div> -->
    <div
      :class="[shrinked ? 'px-9' : 'px-6']"
      class="py-8 border-t border-slate-300 padding-menubar"
      v-show="!userPermission.isFetched"
    >
      <div class="h-[40px] w-full mt-[8px]" v-for="n in 4" :key="'lazyloadmenu-' + n">
        <linearLoadingCard body-class="rounded-[8px]" />
      </div>
    </div>
    <div
      :class="[shrinked ? 'px-9' : 'px-6']"
      class="py-8 border-0 border-slate-300 padding-menubar"
      v-show="userPermission.isFetched"
    >
      <p
        :class="[shrinked ? 'text-center' : 'text-start']"
        class="text-tertiary font-semibold text-sm text-title-menubar"
      >
        Main
      </p>

      <!-- AUTOMATED -->
      <div v-for="menu in menuSets" :key="menu.pageName!">
        <div
          class="absolute bg-white left-[128px] z-50 rounded-md w-[212px] overflow-x-hidden flex flex-col shadow-lg"
        >
          <Transition
            name="fade"
            v-for="submenu in menu.submenu"
            :key="'smallmenu-' + submenu.pageName!"
          >
            <div v-show="menu.subMenuActive" class="flex">
              <button
                v-if="hasPermissions(getModules(menu.submenu, menu.module as moduleName))"
                :class="[
                  shrinked ? 'flex' : 'lg:hidden flex',
                  submenu.pageGroup.includes(currentRouteName) ? 'active' : ''
                ]"
                class="justify-start items-center w-full p-2 text-menubar text-base font-medium menubar-list cursor-pointer"
                @click="() => router.push({ name: submenu.pageName! })"
              >
                <div
                  :class="[
                    submenu.pageGroup.includes(currentRouteName)
                      ? 'menubar-submenu-active'
                      : shrinked ? 'menubar-submenu-active' : 'menubar-submenu',
                    shrinked ? 'ml-0' : 'ml-4'
                  ]"
                  class="my-auto ml-4"
                  width="24px"
                  height="24px"
                ></div>
                <p class="pl-3 w-full text-left hover:text-[--algo-brand-hadfield-blue-500]" :class="[
                    submenu.pageGroup.includes(currentRouteName)
                      ? 'text-[--algo-brand-hadfield-blue-500]'
                      : 'text-[--algo-brand-hadfield-blue-500]',
                  ]">{{ submenu.pageLabel }}</p>
              </button>
            </div>
          </Transition>
        </div>
        <!-- end of SHRINKED menu -->
        <button
          v-if="hasPermissions(getModules(menu.submenu, menu.module as moduleName))"
          :class="[
            shrinked ? 'justify-center' : 'justify-start',
            menu.pageGroup.includes(currentRouteName) ? 'active' : ''
          ]"
          @click="
            () => {
              menu.submenu.length > 0
                ? hideSubmenuExcept(menu.tag!)
                : router.push({ name: menu.pageName! })
            }
          "
          class="flex w-full mt-4 p-2 text-menubar text-base font-medium rounded-md cursor-pointer menubar-list group"
        >
          <div class="relative" width="24px" height="24px">
            <BaseIcon
              :icon-name="menu.icon!"
              :svg-class="
                [
                  menu.pageGroup.includes(currentRouteName)
                    ? 'fill-[--algo-brand-hadfield-blue-500]'
                    : 'fill-white group-hover:fill-white'
                ].join(' ')
              "
            />
            <span 
              v-if="
                menu.submenu.some(item => item.module === moduleName.CIRCLE_RESEARCH) && 
                !fetchState.totalRequest &&
                (communityResearch.totalCircleRequest ?? 0) > 0
              " 
              :class="[
                'absolute rounded-[100px] bg-[--algo-system-red-600] text-[12px] font-bold text-[--algo-base-white] px-[8px] leading-none',
                 shrinked ? 'bottom-[26px] right-[0px] py-[3px]' : 'bottom-[2px] left-[100px] pt-[3.5px] pb-[3px]'
              ]"
            >
              {{ communityResearch.totalCircleRequest }}
            </span>
          </div>

          <div :class="[shrinked ? 'hidden' : 'hidden lg:flex']" class="w-full justify-between">
            <div :class="[shrinked ? 'hidden' : 'hidden lg:block']" class="pl-3 text-left">
              {{ menu.pageLabel }}
            </div>
            <div
              v-if="menu.submenu.length > 0"
              :class="[shrinked ? 'hidden' : 'lg:block hidden']"
              class="my-auto"
            >
              <BaseIcon
                v-if="!menu.subMenuActive"
                :icon-name="menu.subMenuActive ? iconNames.TOP : iconNames.BOT"
                :size="18"
                :svg-class="
                  menu.pageGroup.includes(currentRouteName)
                    ? 'fill-[--algo-brand-hadfield-blue-500]'
                    : 'group-hover:fill-[--algo-brand-hadfield-blue-500]'
                "
              />
              <!-- <BaseIcon
                :icon-name="iconNames.TOP"
                :size="18"
                v-show="menu.subMenuActive"
                svg-class="fill-[#64748B] group-hover:fill-white"
              /> -->
            </div>
          </div>
        </button>
        <!-- SUBMENU SHRINKED FALSE -->
        <div v-for="submenu in menu.submenu" :key="'bigmenu-' + submenu.pageName!">
          <Transition name="fade">
            <div v-show="menu.subMenuActive && !shrinked" class="justify-start hidden lg:block">
              <button
                v-if="
                  Array.isArray(submenu.module!)
                    ? hasPermissions(submenu.module!)
                    : hasPermission(submenu.module!)
                "
                :class="[submenu.pageGroup.includes(currentRouteName) ? 'active' : '']"
                class="flex w-full mt-2 p-2 text-menubar text-base font-medium rounded-md menubar-list cursor-pointer"
                @click="() => router.push({ name: submenu.pageName! })"
              >
                <div
                  :class="[
                    submenu.pageGroup.includes(currentRouteName)
                      ? 'menubar-submenu-active'
                      : 'menubar-submenu',
                    shrinked ? 'ml-0' : 'ml-4'
                  ]"
                  class="my-auto ml-4"
                  width="24px"
                  height="24px"
                ></div>
                <p :class="[shrinked ? 'hidden' : 'block']" class="pl-3 text-list-menubar text-left">
                  {{ submenu.pageLabel }}
                </p>
              </button>
            </div>
          </Transition>
        </div>
      </div>
      <!-- end of AUTOMATED -->
    </div>
  </div>
</template>

<style scoped>
/* width */
::-webkit-scrollbar {
  width: 4px;
}

.fade-enter-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
